import { useEffect, useState } from "react"
import { Button, ButtonGroup, Checkbox, DrawerBody, DrawerFooter, Stack, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Drawer, Form } from "components"
import { API, Moment } from "config"
import { Authenticate } from "screens"

export default function OrdersVisibility({ data, onSuccess, ...props }) {
    const toast = useToast()
    const auth = useDisclosure()
    const [loading, setLoading] = useState(false)
    const [removing, setRemoving] = useState(false)
    const [categories, setCategories] = useState([])

    const [date, setDate] = useState('')
    const [onlySegmented, setOnlySegmented] = useState(false)
    const [selectedCategories, setSelectedCategories] = useState([])

    const current = data?.options?.orders_visible_from

    useEffect(() => {
        if (!data) return
        setDate(data.options?.orders_visible_from ?? '')
        setOnlySegmented(data.options?.orders_only_segmented ?? false)
        setSelectedCategories(data.options?.orders_segmented_categories ?? [])
    }, [data])

    useEffect(() => {
        API.get('/common/categories?separate_order=true')
            .then(res => setCategories(res.data))
            .catch(() => {})
    }, [])

    const handleToggleCategory = (id) => {
        setSelectedCategories(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        )
    }

    const handleSubmit = async () => {
        setLoading(true)
        try {
            await API.put(`/employees/${data?.id}/orders-visibility`, removing ? {
                orders_visible_from: null,
                orders_only_segmented: false,
                orders_segmented_categories: [],
            } : {
                orders_visible_from: date || null,
                orders_only_segmented: onlySegmented,
                orders_segmented_categories: onlySegmented ? selectedCategories : [],
            })
            toast({ title: 'Visibilidade de pedidos atualizada.', status: 'success' })
            onSuccess?.()
            props.onClose()
        } catch (err) {
            toast({
                title: 'Não foi possível atualizar.',
                description: err?.response?.data?.message,
                status: 'warning'
            })
        }
        setLoading(false)
        setRemoving(false)
    }

    const openAuth = (remove = false) => {
        setRemoving(remove)
        auth.onOpen()
    }

    return (
        <>
        <Drawer size="md" loading={loading} {...props}>
            <DrawerBody>
                <Stack spacing={5}>
                    <Text fontSize="sm" color="GrayText">
                        Defina a partir de qual data este funcionário pode ver pedidos.
                        Pedidos anteriores à data ficam invisíveis para ele.
                    </Text>

                    { current && (
                        <Text fontSize="sm" color="orange.500">
                            Restrição atual: pedidos a partir de <b>{Moment(current).format('LL')}</b>
                            { data?.options?.orders_only_segmented && ', apenas pedidos segmentados' }
                            { data?.options?.orders_segmented_categories?.length > 0 && ` (${data.options.orders_segmented_categories.length} categoria(s))` }
                            .
                        </Text>
                    )}

                    <Form.Text
                        type="date"
                        label="Visível a partir de:"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        isDisabled={loading}
                    />

                    <Form.Switch
                        label="Restringir apenas a pedidos segmentados"
                        isChecked={onlySegmented}
                        onChange={e => {
                            setOnlySegmented(e.target.checked)
                            if (!e.target.checked) setSelectedCategories([])
                        }}
                        isDisabled={loading}
                    />

                    { onlySegmented && categories.length > 0 && (
                        <Stack spacing={2}>
                            <Text fontSize="sm" fontWeight="medium">
                                Filtrar por categorias (deixe todas desmarcadas para ver todas):
                            </Text>
                            { categories.map(cat => (
                                <Checkbox
                                    key={cat.id}
                                    colorScheme="orange"
                                    isChecked={selectedCategories.includes(cat.id)}
                                    onChange={() => handleToggleCategory(cat.id)}
                                    isDisabled={loading}
                                >
                                    {cat.name}
                                </Checkbox>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </DrawerBody>

            <DrawerFooter>
                <ButtonGroup isDisabled={loading}>
                    <Button colorScheme="orange" onClick={() => openAuth(false)} isLoading={loading}>
                        Salvar
                    </Button>
                    { current && (
                        <Button colorScheme="red" variant="outline" onClick={() => openAuth(true)}>
                            Remover restrição
                        </Button>
                    )}
                    <Button onClick={props.onClose}>Cancelar</Button>
                </ButtonGroup>
            </DrawerFooter>
        </Drawer>

        <Authenticate {...auth} onSuccess={handleSubmit} />
        </>
    )
}
